import { savePostLoginRedirect } from '~/composables/usePostLoginRedirect';

// The portal already active for this browser (from localStorage). Read
// defensively so non-DOM contexts just fall through to the login redirect.
function getActivePortal(): string {
  if (typeof localStorage === 'undefined') {
    return '';
  }
  try {
    return (localStorage.getItem('selectedPortal') || '').trim().toLowerCase();
  } catch {
    return '';
  }
}

// Exported for testing - handles portal redirect logic
export function handlePortalRedirect(to: any): string | undefined {
  // Skip if no portal param
  if (!to.query.portal) {
    return undefined;
  }

  const portalParam = to.query.portal as string;

  // Handle empty string portal param
  if (!portalParam.trim()) {
    return undefined;
  }

  const requestedPortal = portalParam.trim().toLowerCase();

  // Build clean URL (remove portal parameter, keep others)
  const url = new URL(to.fullPath, window.location.origin);
  url.searchParams.delete('portal');
  const cleanUrl = url.pathname + (url.search || '');

  // No-op switch: the requested portal is already the active one. Do NOT force a
  // full re-login — that strips the other query params (e.g. a Stripe session_id
  // on /payment/success) and bounces the user through Auth0 for nothing. Just
  // strip the redundant ?portal= and stay put on the (cleaned) URL.
  if (getActivePortal() === requestedPortal) {
    return cleanUrl;
  }

  // Switching portals (or none active yet): save the clean URL for after auth,
  // then send the user through the portal-specific login.
  savePostLoginRedirect(cleanUrl, { force: true, source: 'portal-redirect' });

  return `/login/${requestedPortal}`;
}

export default defineNuxtRouteMiddleware((to: any) => {
  // Only run client-side (static site on S3)
  if (!import.meta.client) {
    return;
  }

  const redirectUrl = handlePortalRedirect(to);
  if (redirectUrl) {
    return navigateTo(redirectUrl);
  }
});
